import { Fragment, useEffect, useMemo, useRef, useState, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  X,
  MapPin,
  CalendarDays,
  StickyNote,
  Plus,
  ListFilter,
  Link as LinkIcon,
  CalendarPlus,
  Download,
  Ticket,
  Star,
} from 'lucide-react'
import {
  addDays,
  addMonths,
  endOfDay,
  startOfDay,
  startOfMonth,
  eachDayOfInterval,
  isSameDay,
  isSameMonth,
  isToday,
  isWeekend,
  format,
} from 'date-fns'
import type { CalendarViewProps, CostType, EventRow, ViewMode } from '../types'
import {
  fmtTime,
  fmtRange,
  WEEKDAY_LABELS,
  monthGridRange,
  weekDays,
} from '../lib/dates'
import { hostColor } from '../lib/hostColors'
import { hostLabel } from '../lib/hostLabels'
import { extraKinds } from '../lib/kinds'
import { track, trackAfterPaint } from '../lib/analytics'
import { downloadIcs, googleCalendarUrl } from '../lib/calendarLinks'
import { useFavorites } from '../lib/favorites'
import { dayPath, eventPath, venuePathFor, wantsNewContext } from '../lib/staticPaths'
import { CITY, localityForVenue } from '../lib/areas'
import { writeEventId } from '../lib/urlFilters'
import { popularKey } from '../lib/popularity'

// What the in-component modal is showing: a single event, or a day's list.
type ModalState =
  | { kind: 'event'; event: EventRow }
  | { kind: 'day'; day: Date; events: EventRow[] }
  | null

// A phone day view with fewer events than this opens in the agenda instead
// (see the auto-agenda effects in CalendarView).
const AUTO_AGENDA_UNDER = 6

const MONTH_MAX_CHIPS = 3

// A raw URL is unreadable in a narrow modal, so show the host instead
// ("hammerandstainlyh.com"). It tells you where you're going before you tap,
// which a generic "More info" label does not.
function linkLabel(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}

// Matches Tailwind's `md` breakpoint. Below it the week view shows ONE day, so the
// prev/next arrows step a day at a time instead of jumping a whole week.
const MOBILE_QUERY = '(max-width: 767px)'

function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches
  )
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY)
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches)
    mq.addEventListener('change', onChange)
    setIsMobile(mq.matches)
    return () => mq.removeEventListener('change', onChange)
  }, [])
  return isMobile
}

// onShowPast stays ONE-WAY and lives on CalendarViewProps itself (types.ts):
// the empty-day and empty-agenda affordances turn past events on when a day
// or the agenda has nothing else to show, they never turn them back off. The
// persistent showPast TOGGLE - the header button and the drawer checkbox - was
// removed everywhere (Lisa, 8/19: "it should not be an option in the filter
// drawer at all"), so there's no longer a second control to keep in sync with
// this one; onShowPast is the only way in now.
export function CalendarView(
  props: CalendarViewProps & { controlsSlot: HTMLDivElement | null },
) {
  const {
    controlsSlot,
    events,
    linkedEvent,
    hiddenPast,
    onShowPast,
    activeFilterSummary,
    view,
    onChangeView,
    anchorDate,
    onChangeAnchor,
    onOpenSubmit,
    filterOpen,
    onToggleFilter,
    subscribeSlot,
    loading,
    popularOpens,
  } = props
  const [modal, setModal] = useState<ModalState>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const isMobile = useIsMobile()

  // The date-range button doubles as the DatePicker's open/close trigger (see
  // DatePicker below for why the outside-click handler needs this ref too).
  const rangeButtonRef = useRef<HTMLButtonElement>(null)

  // Close the modal on Escape.
  useEffect(() => {
    if (!modal) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setModal(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [modal])

  // Opens the event a `?event=<id>` link pointed at, once App.tsx has
  // resolved it (see its "Linked event" effect). Ref-guarded so this only
  // ever applies once - closing the modal afterward must not reopen it just
  // because `linkedEvent` is still sitting there.
  const appliedLinkedEvent = useRef(false)
  useEffect(() => {
    if (!linkedEvent || appliedLinkedEvent.current) return
    appliedLinkedEvent.current = true
    setModal({ kind: 'event', event: linkedEvent })
  }, [linkedEvent])

  // Mirrors the modal's event (if any) into `?event=` (lib/urlFilters).
  // Skips the mount-time run: `modal` starts null, and writing that null
  // before the effect above gets a chance to open a `?event=<id>` link would
  // strip the param out from under it before it was ever read.
  const eventUrlMounted = useRef(false)
  useEffect(() => {
    if (!eventUrlMounted.current) {
      eventUrlMounted.current = true
      return
    }
    writeEventId(modal?.kind === 'event' ? modal.event.id : null)
  }, [modal])

  // Sort once by start time; per-day filtering preserves that order.
  const sorted = useMemo(
    () =>
      [...events].sort(
        (a, b) =>
          new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
      ),
    [events]
  )

  // Bucketed ONCE per `sorted`, not once per day cell. `eventsForDay` used to
  // be `sorted.filter(...)` called fresh for every day on screen - cheap for
  // 3 days, but month view calls it ~35-42 times a render and agenda calls it
  // once per day from the anchor forward, so every one of those cells re-scanned
  // the WHOLE list on every render, including the one every keystroke in the
  // search box causes (App.tsx re-renders this with a new `events` reference
  // per character typed). Grouping into a Map first turns that into one O(n)
  // pass plus O(days) lookups, keyed on the same local Y/M/D that isSameDay
  // itself compares, so the bucket a day lands in is identical to what the old
  // per-cell filter would have found.
  const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
  const eventsByDay = useMemo(() => {
    const m = new Map<string, EventRow[]>()
    for (const e of sorted) {
      const k = dayKey(new Date(e.start_time))
      const bucket = m.get(k)
      if (bucket) bucket.push(e)
      else m.set(k, [e])
    }
    return m
  }, [sorted])
  const eventsForDay = (day: Date): EventRow[] => eventsByDay.get(dayKey(day)) ?? []

  // Same question, asked of the events the past toggle is holding back. A day
  // that is empty ONLY because everything on it has finished must not be told
  // "nothing listed for this day yet" - that day was full, and saying otherwise
  // is the same class of wrongness as painting the empty state during a load.
  const pastCountByDay = useMemo(() => {
    const m = new Map<string, number>()
    for (const e of hiddenPast) {
      const k = dayKey(new Date(e.start_time))
      m.set(k, (m.get(k) ?? 0) + 1)
    }
    return m
  }, [hiddenPast])
  const pastForDay = (day: Date): number => pastCountByDay.get(dayKey(day)) ?? 0

  // Mobile renders ONE day, full stop (Andrew, 7/28) - a month grid is unreadable
  // at that width. The month option is gone from the toggle there, but a
  // ?view=month link written on a desktop still opens on a phone, so month is
  // coerced to the day view HERE, at render, rather than by rewriting the state.
  // That keeps the coercion non-destructive: the URL still says month, so
  // rotating to a tablet or opening the same link on a laptop gets the month
  // back. Everything below reads effectiveView, never `view`.
  //
  // Agenda is deliberately NOT coerced. Month degrades because seven columns are
  // unreadable at 375px; the agenda is one column of full-width rows, which is
  // the layout a phone renders BETTER than a laptop. Sending it to the day view
  // there would degrade the format for the device it suits most, and it is the
  // only mode on a phone that gets past today without tapping - the
  // four-taps-to-Saturday problem the day strip was built for.
  const effectiveView: ViewMode = isMobile && view === 'month' ? 'rolling' : view
  // Two real choices on a phone now, so the span control finally has something
  // to offer there: a day at a time, or the run of them.
  const mobileMode: 'day' | 'agenda' = effectiveView === 'agenda' ? 'agenda' : 'day'

  // A phone day view with almost nothing on it is a screenful of whitespace: the
  // 2-event Monday in the Sep 7 week filled about a fifth of the screen and left
  // the rest blank. Under six events (AUTO_AGENDA_UNDER, raised from five on
  // 10/4 per Andrew), open in the agenda instead, which is a
  // continuous run of days and fills the same space with things a reader can
  // actually act on.
  //
  // ONE SHOT, on landing, not on every day change. The day strip is hidden in
  // agenda view, so re-evaluating per day would flip someone into agenda the
  // moment they tapped a quiet day and take away the control they tapped it
  // with. And it defers to any explicit choice: tapping Day pins the view (see
  // the toggle below), and an explicit ?view= never reaches here because it
  // arrives as the initial state and is left alone once this has run.
  const autoAgendaRef = useRef(false)
  useEffect(() => {
    if (autoAgendaRef.current) return
    if (!isMobile || loading) return
    // Nothing loaded yet is not the same as a quiet day.
    if (events.length === 0) return
    autoAgendaRef.current = true
    if (view === 'agenda') return
    if (eventsForDay(anchorDate).length < AUTO_AGENDA_UNDER) {
      // After paint: this fires from an effect in the commit that first renders
      // the loaded events, the same forced-layout trap as onboarding_shown (see
      // trackAfterPaint). Not cancelled on cleanup on purpose: the ref above
      // means this runs once, and the view change it records does happen.
      trackAfterPaint('view_changed', { to: 'agenda', label: 'Agenda', mobile: true, auto: true })
      onChangeView('agenda')
    }
    // eventsForDay is derived from `events` and changes with it; the ref is what
    // makes this run once, not the dependency list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, loading, events, anchorDate, view])

  // The same check again whenever the FILTERS change (Andrew, 10/4): ticking
  // Powhatan, a 2-events-a-day area, left a phone on a near-empty day because
  // the switch above had already spent its one shot on landing. A new filter
  // is a new question, so it re-asks even after an explicit Day tap. Keyed on
  // the summary label, which changes exactly when the filter set does; clearing
  // every filter (null) leaves the view alone. Day taps still never re-trigger
  // it, for the reason given above.
  const lastFilterRef = useRef(activeFilterSummary)
  useEffect(() => {
    if (!isMobile || loading || events.length === 0) return
    if (lastFilterRef.current === activeFilterSummary) return
    lastFilterRef.current = activeFilterSummary
    if (activeFilterSummary === null || view === 'agenda') return
    if (eventsForDay(anchorDate).length < AUTO_AGENDA_UNDER) {
      trackAfterPaint('view_changed', { to: 'agenda', label: 'Agenda', mobile: true, auto: true, cause: 'filter' })
      onChangeView('agenda')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, loading, events, activeFilterSummary, anchorDate, view])

  const step = (dir: number) => {
    track('date_nav', { direction: dir > 0 ? 'next' : 'prev', view: effectiveView, mobile: isMobile })
    if (effectiveView === 'month') {
      onChangeAnchor(addMonths(anchorDate, dir))
      return
    }
    // Every remaining mode is anchor-relative (3 days, 7 days, the single day
    // mobile renders, and the agenda's starting point), so they all step exactly
    // one day.
    onChangeAnchor(addDays(anchorDate, dir))
  }

  const rangeLabel = (): string => {
    if (effectiveView === 'month') return format(anchorDate, 'MMMM yyyy')
    // The agenda has no end, so naming one would be a lie. It says where the
    // list STARTS, which is also the only thing the arrows and the picker move.
    // The "From" prefix is dropped on PHONES only (Andrew, 2026-09-07). There the
    // button shares one bar with the mark, Filters, the view toggle and the menu,
    // and the word was explaining the control rather than labelling it. Desktop
    // has the room and keeps it.
    if (effectiveView === 'agenda') {
      return isMobile ? format(anchorDate, 'MMM d') : `From ${format(anchorDate, 'MMM d')}`
    }
    // Both day-span modes render a single day on mobile.
    // 'MMM d', not 'EEE, MMM d'. The weekday moved out of this label on 2026-09-07
    // when the controls joined the top bar: the day strip sits directly under it
    // with the weekday letter over every date and the selected one filled, so the
    // "Mon," prefix was saying twice what the row below says once - and it was
    // costing the ~30px that made this button truncate to "Mon, Se...".
    if (isMobile) return format(anchorDate, 'MMM d')
    const e = addDays(anchorDate, effectiveView === '3day' ? 2 : 6)
    const start = format(anchorDate, 'MMM d')
    const end = isSameMonth(anchorDate, e) ? format(e, 'd') : format(e, 'MMM d')
    return `${start} – ${end}`
  }

  // The span toggle is desktop-only now, so the highlight just follows whatever
  // is actually being rendered.
  const activeView: ViewMode = effectiveView

  // The controls row. On a phone it renders through a portal into TopBar's
  // slot (Andrew, 2026-09-07: "move the content in the bar with the filters to
  // the top bar in that empty space") - a portal rather than lifting 240 lines
  // of JSX and fourteen pieces of local state up two components, so the row
  // keeps its own code and just renders somewhere else. Desktop is untouched:
  // no slot, no portal, same sticky header it always had.
  const inTopBar = isMobile && controlsSlot !== null
  const controlsBar = (
        <header
          className={
          inTopBar
            // Inside the top bar: no sticky, no page padding, no background of
            // its own. It is a row inside that bar now, not a bar.
            // justify-between, not justify-end: Filters anchors to the left of
            // the slot so it keeps a fixed 5px from the mark, and the view
            // toggle is pushed to the right against the menu button. Under
            // justify-end the whole row slid right whenever its content got
            // narrower - dropping "From" from the agenda label opened a 41px
            // hole between the mark and Filters.
            ? 'flex min-w-0 flex-1 items-center justify-between gap-1'
            : 'sticky top-0 z-20 -mx-2 flex shrink-0 items-center justify-between gap-1.5 bg-paper px-2 py-2 md:static md:mx-0 md:gap-2 md:px-0 md:pt-2 md:pb-2 lg:pt-0 dark:bg-slate-950'
        }
        >
          <div className="relative flex min-w-0 items-center gap-1.5 md:gap-2">
            {/* Only while the drawer is CLOSED. Open, the drawer carries its own X,
                so two controls were competing to close the same thing. Unmounting
                shifts the rest of the row across, which is the accepted cost of not
                leaving a dead toggle sitting beside an open panel. */}
            {!filterOpen && (
              <button
                type="button"
                onClick={() => {
                  track('filter_toggled', { opening: true })
                  onToggleFilter()
                }}
                title="Filters"
                // Filled brick at EVERY width (Andrew, 9/3). It used to revert to the
                // quiet hairline chrome from md up, on the theory that a desktop
                // header has room for it to read as a control among controls - but
                // that gave the same button two personalities, and the desktop one
                // receded into the prev/Today/next cluster next to it. Filtering is
                // the thing a week of events most needs, so it carries the brand
                // fill on a laptop for the same reason it does on a phone. White on
                // brick-600 (#7a2e3e) is 9.18:1, and the only md: class left is the
                // height, which keeps it level with the chrome beside it.
                // min-h-8 (32px) on phones: the height of the Day/Agenda
                // BUTTON beside it, not of the group that wraps it. Matching the
                // wrapper's 38 was the wrong reference - that measurement
                // includes its border and p-0.5, so Filters came out 6px taller
                // than the black button it is meant to sit level with.
                // Desktop still clears it via md:min-h-0.
                className="flex min-h-8 shrink-0 items-center justify-center gap-1.5 rounded-lg border border-brick-600 bg-brick-600 px-2 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brick-700 md:min-h-0"
              >
                <ListFilter className="h-3.5 w-3.5" />
                {/* Label shows at EVERY width. It was `hidden sm:inline`, which left
                    phones with a bare icon sitting against the prev/Today/next cluster,
                    so it read as one more date control. 80% of launch-day traffic was
                    mobile and only 2 of 19 visitors ever opened the filter.
                    Plural (Lisa, 7/28): the panel behind it holds six of them. */}
                <span>Filters</span>
              </button>
            )}
  
            {/* Desktop only (Lisa, 8/19): the day strip below already gives a
                phone one-tap access to any day in the visible week, and the range
                button opens the picker for anything further out, so the
                prev/Today/next cluster was a second, redundant way to do the same
                job in a header that's already full at 375px. */}
            <div className="hidden shrink-0 items-center rounded-lg border border-hairline md:flex dark:border-slate-800">
              <button
                type="button"
                aria-label="Previous"
                onClick={() => step(-1)}
                className="inline-flex min-h-10 min-w-10 items-center justify-center p-1 text-slate-500 hover:text-slate-900 md:min-h-0 md:min-w-0 dark:text-slate-400 dark:hover:text-slate-100"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => {
                  track('today_clicked', { view: effectiveView })
                  onChangeAnchor(new Date())
                }}
                className="min-h-10 border-x border-hairline px-2 py-0.5 text-xs font-medium text-slate-600 hover:text-slate-900 md:min-h-0 md:px-2 dark:border-slate-800 dark:text-slate-300 dark:hover:text-slate-100"
              >
                Today
              </button>
              <button
                type="button"
                aria-label="Next"
                onClick={() => step(1)}
                className="inline-flex min-h-10 min-w-10 items-center justify-center p-1 text-slate-500 hover:text-slate-900 md:min-h-0 md:min-w-0 dark:text-slate-400 dark:hover:text-slate-100"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            {/* The range doubles as the date picker's trigger - see DatePicker for
                why it hangs off this and not Today. */}
            <button
              ref={rangeButtonRef}
              type="button"
              onClick={() => {
                if (!pickerOpen) track('date_picker_opened', { view: effectiveView })
                setPickerOpen((o) => !o)
              }}
              aria-expanded={pickerOpen}
              aria-haspopup="dialog"
              title="Jump to a date"
              className="flex min-w-0 items-center gap-1 rounded-md px-1 py-0.5 text-slate-900 transition-colors hover:bg-white dark:text-slate-100 dark:hover:bg-slate-800"
            >
              <h1 className="truncate text-sm font-semibold">{rangeLabel()}</h1>
              <ChevronDown
                aria-hidden="true"
                className={`h-3.5 w-3.5 shrink-0 opacity-60 transition-transform ${
                  pickerOpen ? 'rotate-180' : ''
                }`}
              />
            </button>
  
            {/* The key belongs beside the dates (Lisa, 7/28). It used to sit at the
                far right of the header, past the span toggle and Submit event,
                which read as one more control rather than as the legend for the
                marks on every card. shrink-0 keeps it whole and lets the range
                label do the truncating when the row runs out of room.
                Desktop only: a phone header is already carrying Filters, the
                prev/Today/next cluster and the range in the space this needs, so
                mobile keeps its own centred line just below. */}
            {/* From lg, not md. The header was already over-subscribed at 768: the
                range label truncates there on the live site TODAY, and adding the
                past toggle squeezed it to 8px - a date you can't read, on the
                button that opens the date picker. Between md and lg the key is the
                only thing here that isn't a control, so it's what stands down, and
                the dates get their full width back. That band is 6 sessions in the
                last 30 days against 367 phone and 193 desktop, so it's the
                cheapest place in the layout to give something up. The phone keeps
                its own copy on the row below. */}
            <CostKey className="hidden shrink-0 items-center gap-2.5 text-xs text-slate-500 lg:flex dark:text-slate-400" />
  
            {/* Anchored to the left CLUSTER, not to the range button. On a phone
                the range sits about 200px in, and a 256px popover hung off it
                would run past the right edge; from here it always opens against
                the header's own left margin. */}
            {pickerOpen && (
              <DatePicker
                anchorDate={anchorDate}
                excludeRef={rangeButtonRef}
                onClose={() => setPickerOpen(false)}
                onPick={(d) => {
                  track('date_picked', {
                    view: effectiveView,
                    days_from_today: Math.round(
                      (startOfDay(d).getTime() - startOfDay(new Date()).getTime()) / 86400000,
                    ),
                  })
                  onChangeAnchor(d)
                  setPickerOpen(false)
                }}
              />
            )}
          </div>
  
          <div className="flex shrink-0 items-center gap-2">
            {/* Day/Agenda switch, MOBILE ONLY. It used to sit on its own row under
                the day strip, alongside the Past toggle and the colour key - both
                of those are gone (Lisa, 8/19), which left a two-button control
                holding a whole 38px row to itself while the header above it ran
                half empty between the range label and Submit event. Moved up here
                (Andrew, 9/3): it takes the slack in the header row, it sits in the
                same slot the desktop span toggle occupies (the two are mutually
                exclusive, md:hidden vs hidden md:flex), and the list gets the row
                back. px-2, not the old px-2.5, because the header row is the tight
                one - at 375px this shares it with Filters, the range and Submit. */}
            <div
              className="flex shrink-0 rounded-lg border border-hairline p-0.5 md:hidden dark:border-slate-800"
              role="group"
              aria-label="Choose a view"
            >
              {(
                [
                  { m: 'day', v: 'rolling', label: 'Day' },
                  { m: 'agenda', v: 'agenda', label: 'Agenda' },
                ] as const
              ).map(({ m, v, label }) => (
                <button
                  key={m}
                  type="button"
                  aria-pressed={mobileMode === m}
                  onClick={() => {
                    track('view_changed', { to: v, label, mobile: true })
                    // Pins the choice against the auto-agenda switch above. Without
                    // this, tapping Day on a quiet day would be undone on the next
                    // render.
                    autoAgendaRef.current = true
                    // Both land on today, matching the desktop toggle: a mode switch
                    // answers "what's on now", not "what was on wherever I'd browsed to".
                    onChangeAnchor(startOfDay(new Date()))
                    onChangeView(v)
                  }}
                  className={`min-h-8 rounded-md px-2 py-1 text-xs font-medium transition ${
                    mobileMode === m
                      ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                      : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
  
            {/* Segmented span toggle, DESKTOP ONLY. Mobile renders a single day in
                every mode and month went with the rest (Andrew, 7/28 - a month
                grid is useless at that size), which leaves a phone with exactly
                one view. A one-option segmented control is not a choice, so the
                whole thing is hidden there and the header gets the width back for
                the range and the date picker.
                The values stay '3day' / 'rolling' / 'month' so the ?view= links
                already sitting in public comments keep working. */}
            <div className="hidden shrink-0 rounded-lg border border-hairline p-0.5 md:flex dark:border-slate-800">
              {(
                [
                  { v: '3day', label: '3 days' },
                  { v: 'rolling', label: '7 days' },
                  { v: 'month', label: 'Month' },
                  { v: 'agenda', label: 'Agenda' },
                ] as const
              ).map(({ v, label }) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => {
                    track('view_changed', { to: v, label })
                    // Anchor the day-span modes on today so the first column is today.
                    if (v !== 'month') onChangeAnchor(startOfDay(new Date()))
                    onChangeView(v)
                  }}
                  className={`min-h-9 rounded-md px-2.5 py-1.5 text-xs font-medium transition md:min-h-0 md:px-2 md:py-0.5 ${
                    activeView === v
                      ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                      : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
  
            <button
              type="button"
              onClick={() => {
                track('submit_event_opened', { from: 'calendar_header' })
                onOpenSubmit()
              }}
              title="Submit event"
              // md:h-[26px] matches the span toggle's rendered height (border
              // + p-0.5 + the buttons' py-0.5) so the two controls line up; the fixed
              // height only applies on desktop, mobile keeps the min-h-9 tap target.
              // Gone below md (Andrew, 2026-09-07: "remove the button with the +
              // icon"). The bottom tab bar carries Add event on a phone, so this
              // was the same action twice on one screen, and the top bar needs
              // the width now that the calendar's controls share it.
              className="hidden min-h-9 shrink-0 items-center justify-center gap-1.5 rounded-lg bg-brick-600 px-2.5 py-1.5 text-xs font-medium text-white transition-colors hover:bg-brick-700 md:inline-flex md:min-h-0 md:h-[26px] md:py-0"
            >
              <Plus className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Submit event</span>
            </button>
          </div>
        </header>
  )

  return (
    <div className="flex min-h-0 flex-col text-slate-700 md:h-full dark:text-slate-200">
      {/* Header. Sticky on mobile, where the whole page scrolls under it. */}
      {/* md:pt-2 gives the tablet layout (no sidebar) a small gap under the
          banner. At lg the calendar COLUMN already adds lg:pt-2, and the sidebar
          starts at its own lg:pt-2, so the header drops its top padding there
          (lg:pt-0) to sit level with "What people want more of" rather than 8px
          lower. */}
      {inTopBar ? createPortal(controlsBar, controlsSlot) : controlsBar}

      {/* Mobile day strip. Mobile's week view is ONE day and the arrows step a
          single day, so reaching Saturday from a Tuesday visit took four taps -
          three mobile visitors rage-clicked the Next arrow on launch day doing
          exactly that, and date_nav ran 23 uses across 19 visitors (friction,
          not engagement). This makes any day of the week one tap. The digest
          leads with "Saturday carries the week", so getting to Saturday is the
          single most likely thing someone arrives wanting. */}
      {/* Not in the agenda, which is a continuous run of days and carries its own
          heading for each one - a week-picker above it would be pointing at
          something already on screen, and dropping it hands the list back the
          52px it costs. */}
      {effectiveView !== 'month' && effectiveView !== 'agenda' && isMobile && (
        <div
          // Just the second bar (Andrew, 2026-09-07: "don't make that a JS
          // dropdown dropup, just have it be fixed as the new second bar").
          // It used to hide on scroll down and slide back on scroll up, which
          // meant a scroll listener, a direction heuristic and a transform that
          // could collide with the row above it. The controls moved up into the
          // top bar, so this is the only sticky thing left in the scroller and
          // can simply stay put - no listener, nothing to collide with.
          className="sticky top-0 z-20 mb-2 flex shrink-0 gap-1 bg-paper pt-1 md:hidden dark:bg-slate-950"
          role="group"
          aria-label="Jump to a day"
        >
          {weekDays(anchorDate).map((day) => {
            const count = eventsForDay(day).length
            const active = isSameDay(day, anchorDate)
            // A filtered link lands on the first day WITH events, which may not be
            // today, so today carries a brick ring to stay locatable. The dark
            // active fill wins when today is the selected day.
            const today = isToday(day)
            return (
              <button
                key={day.toISOString()}
                type="button"
                aria-pressed={active}
                // No aria-label (2026-09-23). It used to carry the whole spoken
                // name, which replaced the visible "W 23 46" entirely, so someone
                // using voice control who says "click 23" found nothing to click
                // (PageSpeed's label-content-name-mismatch, all 7 buttons). The
                // name now comes from the content, an sr-only span at the end that
                // reads the same as before, "Wednesday September 23 (today), 46
                // events", and still contains the visible "23" and "46".
                onClick={() => {
                  track('day_strip_tapped', { to: format(day, 'EEE'), count })
                  onChangeAnchor(day)
                }}
                // Three states on the paper ground: the selected day keeps the
                // solid dark fill, today gets a brick ring and no fill (matching
                // the brick today-circle in the desktop day headers), and the
                // rest stay transparent so the strip reads as part of the page
                // instead of a row of grey chips.
                className={`flex min-h-11 flex-1 flex-col items-center justify-center rounded-lg border py-1 transition-colors ${
                  active
                    ? 'border-slate-900 bg-slate-900 text-white dark:border-slate-100 dark:bg-slate-100 dark:text-slate-900'
                    : today
                      ? 'border-brick-300 text-brick-700 hover:bg-white dark:border-brick-300 dark:text-brick-300'
                      : 'border-hairline text-slate-600 hover:bg-white dark:border-slate-800 dark:text-slate-300 dark:hover:bg-slate-800'
                }`}
              >
                {/* The letter and the count used to be dimmed with opacity-70 and
                    opacity-60, and at 10px and 9px. Composited over paper that
                    measured 3.48:1 and 2.82:1 on a plain day and 3.38:1 for the
                    count on today (PageSpeed flagged the counts, 2026-09-23), the
                    smallest text on the page carrying the least contrast. Both now
                    take the button's own colour at full strength (7.18:1 on a
                    plain day, 9.56:1 today, 17.83:1 selected) and 11px. The day
                    number still leads because it is larger and semibold, which
                    was always what carried the hierarchy. The strip is ~3px
                    taller for it; it has no fixed height to break. */}
                <span aria-hidden="true" className="text-[11px] font-medium uppercase tracking-wide">
                  {format(day, 'EEEEE')}
                </span>
                <span aria-hidden="true" className="text-sm font-semibold leading-tight">
                  {format(day, 'd')}
                </span>
                {/* A count, not a dot: "which day is busiest" is the question
                    someone scanning a week actually has. 0 stays blank rather
                    than showing a zero, so empty days recede instead of shouting;
                    the spoken name below still says "0 events". */}
                <span aria-hidden="true" className="text-[11px] font-medium leading-none">
                  {count > 0 ? count : ' '}
                </span>
                {/* The whole spoken name in ONE sr-only span, with the visible
                    pieces aria-hidden. Split across several sr-only spans, Chrome
                    pads each one with spaces (they are absolutely positioned, so
                    they count as blocks) and it read "23 , 46 events". */}
                <span className="sr-only">
                  {`${format(day, 'EEEE MMMM d')}${today ? ' (today)' : ''}, ${count} events`}
                </span>
              </button>
            )
          })}
        </div>
      )}

      {/* Body */}
      <div className="min-h-0 flex-1">
        {/* event_id joins event_name/host/venue on every event_opened capture
            below (4 call sites) as of 2026-09-11 - lyh_event_popularity is
            keyed on name only because the ORIGINAL capture never sent an id
            (see its migration), which is a real limitation there. Sending the
            id from here on means that table can be re-keyed on it later
            instead of staying name-keyed forever. */}
        {effectiveView === 'agenda' ? (
          <AgendaList
            anchorDate={anchorDate}
            events={sorted}
            hiddenPast={hiddenPast}
            onShowPast={onShowPast}
            subscribeSlot={subscribeSlot}
            loading={loading}
            popularOpens={popularOpens}
            onOpenEvent={(event) => { track('event_opened', { event_id: event.id, event_name: event.name, host: event.host, venue: event.venue }); setModal({ kind: 'event', event }) }}
          />
        ) : effectiveView !== 'month' ? (
          <WeekGrid
            view={effectiveView}
            anchorDate={anchorDate}
            eventsForDay={eventsForDay}
            pastForDay={pastForDay}
            onShowPast={onShowPast}
            sortedEvents={sorted}
            hiddenPast={hiddenPast}
            filterSummary={activeFilterSummary}
            onJump={onChangeAnchor}
            subscribeSlot={subscribeSlot}
            loading={loading}
            popularOpens={popularOpens}
            onOpenEvent={(event) => { track('event_opened', { event_id: event.id, event_name: event.name, host: event.host, venue: event.venue }); setModal({ kind: 'event', event }) }}
          />
        ) : (
          <MonthGrid
            anchorDate={anchorDate}
            eventsForDay={eventsForDay}
            onOpenEvent={(event) => { track('event_opened', { event_id: event.id, event_name: event.name, host: event.host, venue: event.venue }); setModal({ kind: 'event', event }) }}
            onOpenDay={(day, dayEvents) => {
              // Month view: tapping a day's dot cluster or its "+N more" overflow
              // opens the day list. Autocapture showed real "+30 / +17" taps on
              // busy days with zero named event behind them - this is how month
              // users drill in, and it was invisible.
              track('day_expanded', {
                count: dayEvents.length,
                weekday: format(day, 'EEE'),
              })
              setModal({ kind: 'day', day, events: dayEvents })
            }}
          />
        )}
      </div>

      {modal && <Modal state={modal} onClose={() => setModal(null)} onOpenEvent={(event) => { track('event_opened', { event_id: event.id, event_name: event.name, host: event.host, venue: event.venue }); setModal({ kind: 'event', event }) }} />}
    </div>
  )
}

// The legend for the bars and dots. Reads its swatches straight off COST_BAR so
// the key can never drift from the marks it explains - all three of them, now
// that promo has a colour of its own.
const KEY_ENTRIES: { type: CostType; label: string }[] = [
  { type: 'free', label: 'Free' },
  { type: 'ticketed', label: 'Paid' },
  { type: 'promo', label: 'Deal' },
]

// The persistent Past toggle (header button + drawer checkbox) was removed
// entirely (Lisa, 8/19: "remove the button showing Past events" / "it should
// not be an option in the filter drawer at all"). Past events now stay hidden
// unless an empty day or an empty agenda offers them - see onShowPast, which
// is still one-way for the reason it always was: a toggle in its place would
// let a stray second tap re-empty the day someone had just filled.

function CostKey({ className }: { className?: string }) {
  return (
    <div className={className} aria-label="What the colours mean">
      {KEY_ENTRIES.map(({ type, label }) => (
        <span key={type} className="inline-flex items-center gap-1">
          <span aria-hidden="true" className={`h-2 w-2 rounded-full ${COST_BAR[type]}`} />
          {label}
        </span>
      ))}
    </div>
  )
}

// A small month grid for jumping somewhere far away. The prev/next arrows step
// one day (or one month), so reaching "three Saturdays from now" was a dozen
// taps; this makes it one.
//
// It hangs off the DATE RANGE rather than the Today button. Today is a verb - it
// has one job and does it - and hiding a second behaviour behind it would break
// a control that already works. The range is a label that says where you are,
// which makes it the thing to press to go somewhere else.
function DatePicker({
  anchorDate,
  onPick,
  onClose,
  excludeRef,
}: {
  anchorDate: Date
  onPick: (d: Date) => void
  onClose: () => void
  /** The range button that opens this popover. It sits OUTSIDE the popover's own
      DOM subtree, so without this the outside-click handler below closed the
      picker on the button's own mousedown, and the button's onClick (a plain
      toggle) then reopened it a beat later - clicking the button to close never
      actually worked (Lisa, 8/19). Excluding clicks on the trigger lets the
      button's own toggle be the only thing that opens or closes it. */
  excludeRef?: RefObject<HTMLElement | null>
}) {
  const [cursor, setCursor] = useState(() => startOfMonth(anchorDate))
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    // mousedown, not click: a click listener would fire on the same event that
    // opened the popover and close it again immediately.
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (ref.current?.contains(target)) return
      if (excludeRef?.current?.contains(target)) return
      onClose()
    }
    window.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onDown)
    return () => {
      window.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onDown)
    }
  }, [onClose, excludeRef])

  const { start, end } = monthGridRange(cursor)
  const days = eachDayOfInterval({ start, end })

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="Jump to a date"
      // z-20 matches the header it sits inside. The drawer is z-40 and its scrim
      // z-30, and anything in this header has to stay under those or it paints
      // through an open filter panel.
      className="absolute left-0 top-full z-20 mt-1 w-64 rounded-lg border border-hairline bg-paper p-2 shadow-lg dark:border-slate-800 dark:bg-slate-900"
    >
      <div className="mb-1 flex items-center justify-between">
        <button
          type="button"
          aria-label="Previous month"
          onClick={() => setCursor((c) => addMonths(c, -1))}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-white hover:text-slate-900 dark:hover:bg-slate-800"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-xs font-semibold text-slate-900 dark:text-slate-100">
          {format(cursor, 'MMMM yyyy')}
        </span>
        <button
          type="button"
          aria-label="Next month"
          onClick={() => setCursor((c) => addMonths(c, 1))}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-white hover:text-slate-900 dark:hover:bg-slate-800"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-7">
        {WEEKDAY_LABELS.map((d) => (
          <div
            key={d}
            className="pb-1 text-center text-[10px] font-medium uppercase tracking-wide text-slate-600 dark:text-slate-500"
          >
            {d.slice(0, 1)}
          </div>
        ))}
        {days.map((day) => {
          const inMonth = isSameMonth(day, cursor)
          const today = isToday(day)
          const picked = isSameDay(day, anchorDate)
          return (
            <button
              key={day.toISOString()}
              type="button"
              aria-label={format(day, 'EEEE MMMM d, yyyy')}
              aria-current={today ? 'date' : undefined}
              onClick={() => onPick(startOfDay(day))}
              // TODAY takes the brick fill even when it is also the day you are
              // parked on. The two facts are independent - "where I am" and
              // "where now is" - and letting the selection paint over today lost
              // the one marker that is the same everywhere else in the app. When
              // they coincide, the ring says both.
              className={`m-px inline-flex h-8 items-center justify-center rounded-md text-xs transition-colors ${
                today
                  ? 'bg-brick-600 font-semibold text-white hover:bg-brick-700'
                  : picked
                    ? 'bg-slate-900 font-semibold text-white dark:bg-slate-100 dark:text-slate-900'
                    : inMonth
                      ? 'text-slate-700 hover:bg-white dark:text-slate-200 dark:hover:bg-slate-800'
                      : 'text-slate-300 hover:bg-white dark:text-slate-600'
              } ${
                picked && today ? 'ring-2 ring-inset ring-slate-900 dark:ring-slate-100' : ''
              }`}
            >
              {format(day, 'd')}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ---- Week view ----

interface GridProps {
  anchorDate: Date
  eventsForDay: (day: Date) => EventRow[]
  onOpenEvent: (e: EventRow) => void
}

function WeekGrid({
  view,
  anchorDate,
  eventsForDay,
  pastForDay,
  onShowPast,
  sortedEvents,
  hiddenPast,
  filterSummary,
  onJump,
  onOpenEvent,
  subscribeSlot,
  loading,
  popularOpens,
}: GridProps & {
  view: ViewMode
  pastForDay: (day: Date) => number
  onShowPast: () => void
  /** Every shown event (not just this window's), sorted ascending by start
      time - what the empty-window hint below searches forward through. */
  sortedEvents: EventRow[]
  hiddenPast: EventRow[]
  filterSummary: string | null
  onJump: (d: Date) => void
  subscribeSlot?: React.ReactNode
  loading?: boolean
  /** Only set while Popular is active - passed through to the mobile day
      list, which renders AgendaRow (see CalendarViewProps in types.ts). The
      desktop grid cards (EventCard) never take it; the badge belongs on the
      row built to carry supporting metadata, not the 137px column tile. */
  popularOpens?: Map<string, number>
}) {
  const isMobile = useIsMobile()
  // Mobile shows a single day (the anchor) so the prev/next arrows step day by day.
  // Desktop shows 3 or 7 days STARTING on the anchor - never snapped to a calendar
  // week, which is what the retired Week view did and what would have made a
  // one-day step a no-op.
  const span = view === '3day' ? 3 : 7
  const days = isMobile
    ? [anchorDate]
    : eachDayOfInterval({ start: anchorDate, end: addDays(anchorDate, span - 1) })

  // The empty-window hint: every column above is about to render its own
  // "Nothing listed for this day yet", which is technically true and still the
  // wrong answer when a filter is on and the thing it matched just isn't in
  // THESE days - Andrew filtering to a place whose next event was 5 days past
  // a 3-day window read as "this org has nothing on the calendar" when it had
  // something the very next week. `days` above is the exact window every
  // column renders from, so counting eventsForDay across it can never disagree
  // with what's on screen.
  //
  // Gated on `filterSummary`: with nothing filtering, an empty window is just
  // an empty window and the per-day copy below is already the right answer -
  // there's no filter to name and no "next match" framing would make sense.
  // Gated on `!loading` too, or the skeleton's brief window with events still
  // in flight would read as a confirmed zero-match window.
  const windowEnd = endOfDay(days[days.length - 1])
  const windowEventCount = days.reduce((n, d) => n + eventsForDay(d).length, 0)
  const showEmptyWindowHint = !loading && filterSummary !== null && windowEventCount === 0

  // sortedEvents is ascending, so the first one past the window's end is the
  // soonest match - exactly the "Next up" the message promises.
  const nextMatch = showEmptyWindowHint
    ? (sortedEvents.find((e) => new Date(e.start_time) > windowEnd) ?? null)
    : null
  // Only worth finding once there's no match ahead: a forward match always
  // wins the framing ("Next up"), and hiddenPast isn't sorted, so this only
  // does the scan when it's actually going to be shown.
  const lastMatch =
    showEmptyWindowHint && !nextMatch
      ? hiddenPast.reduce<EventRow | null>(
          (latest, e) =>
            !latest || new Date(e.start_time) > new Date(latest.start_time) ? e : latest,
          null,
        )
      : null

  if (showEmptyWindowHint && (nextMatch || lastMatch)) {
    const match = (nextMatch ?? lastMatch)!
    return (
      <div className="flex min-h-56 flex-col items-center justify-center gap-2 rounded-lg border border-hairline bg-white px-4 py-10 text-center md:h-full dark:border-slate-800 dark:bg-slate-900">
        <span className="text-sm text-slate-400 dark:text-slate-500">
          Nothing in these days for {filterSummary}.
        </span>
        <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
          {nextMatch ? 'Next up: ' : 'Last was '}
          {match.name}, {format(new Date(match.start_time), 'EEE, MMM d')}
        </span>
        {nextMatch && (
          <button
            type="button"
            onClick={() => {
              track('filter_empty_window_jump', { view, mobile: isMobile })
              onJump(startOfDay(new Date(nextMatch.start_time)))
            }}
            className="rounded-md border border-hairline px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-paper dark:border-slate-800 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            Jump to it
          </button>
        )}
      </div>
    )
  }

  return (
    // Both column classes are written out in full: Tailwind scans source text, so a
    // built-up `md:grid-cols-${n}` would never make it into the stylesheet.
    <div
      className={`grid min-h-0 grid-cols-1 gap-1.5 md:h-full md:gap-1 ${
        view === '3day' ? 'md:grid-cols-3' : 'md:grid-cols-7'
      }`}
    >
      {days.map((day) => {
        const dayEvents = eventsForDay(day)
        const past = pastForDay(day)
        const today = isToday(day)
        return (
          <div
            key={day.toISOString()}
            // Weekends get a whisper of the brand brick so Sat/Sun read as the
            // days most people are actually planning around (Andrew, 8/6).
            className={`flex min-h-0 flex-col overflow-hidden rounded-lg border border-hairline dark:border-slate-800 ${
              isWeekend(day) ? 'bg-brick-50 dark:bg-brick-900/20' : 'bg-white dark:bg-slate-900'
            }`}
          >
            {/* Hidden below md, where the grid renders a SINGLE day: the calendar
                header already reads "Thu, Jul 23" and the day strip highlights the
                same day, so this repeated it three times and cost vertical space on
                the smallest screens. md matches MOBILE_QUERY (max-width 767px)
                exactly, so it flips in step with the single-day logic. */}
            {/* Weekday and date on ONE line ("WED 30"). Stacked, they cost a
                whole text row per column for no information - and that row is
                one more event visible in a day that routinely runs 15-30. */}
            {/* An <a> to the day's own pre-rendered page. This was inert text,
                so no interaction changes for anyone using the app; what it adds
                is the only in-app link to the 93 /day/ pages, which Google had
                no path to except the sitemap. Kept desktop-only exactly as
                before - the mobile grid renders a single day and the header
                already names it. */}
            <a
              href={dayPath(format(day, 'yyyy-MM-dd'))}
              className="hidden shrink-0 items-center justify-center gap-1 border-b border-hairline transition-colors hover:bg-white md:flex md:px-1 md:py-1 dark:border-slate-800 dark:hover:bg-slate-800"
            >
              <span className="text-[10px] font-medium uppercase tracking-wide text-slate-600 dark:text-slate-500">
                {format(day, 'EEE')}
              </span>
              <span
                className={`inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-xs font-semibold ${
                  today
                    ? 'bg-brick-600 text-white'
                    : 'text-slate-700 dark:text-slate-200'
                }`}
              >
                {format(day, 'd')}
              </span>
            </a>
            <div className="min-h-0 flex-1 space-y-1 p-1 md:overflow-y-auto">
              {loading && dayEvents.length === 0 ? (
                /* Skeleton, NOT the empty state. Before this, "Nothing listed for
                   this day yet" rendered on every cold load - events start as []
                   while the fetch is in flight, so the first paint told people the
                   day was empty and then flipped to a full list. Saying "nothing
                   on" when we simply haven't looked yet is worse than saying
                   nothing: it's the one message someone might act on by leaving. */
                /* 10 bars, not 3: a typical day here runs 15-30 events, so three
                   short bars implied a nearly-empty day and the list jumped when
                   it filled. Roughly filling the screen makes the skeleton read
                   as "loading a lot" and stops the layout lurching. */
                <div aria-hidden="true" className="space-y-1 p-1">
                  {Array.from({ length: 10 }, (_, i) => (
                    <div
                      key={i}
                      className="h-10 animate-pulse rounded-md bg-slate-100 md:h-6 dark:bg-slate-800"
                      style={{ opacity: 1 - i * 0.07 }}
                    />
                  ))}
                </div>
              ) : dayEvents.length === 0 && past > 0 ? (
                /* Empty only because everything here has finished. "Nothing
                   listed for this day yet" would be a flat lie on a day that ran
                   fifteen events, and it's the message someone lands on the
                   moment they step back a day - so say what happened and put the
                   way back one tap away, rather than making them find a checkbox
                   in a drawer that almost nobody opens. */
                <div className="flex flex-col items-center justify-center gap-1.5 px-2 py-4 text-center md:h-full md:gap-1 md:py-0">
                  <span className="text-xs leading-tight text-slate-400 md:text-[10px] dark:text-slate-500">
                    <span className="md:hidden">
                      {past} {past === 1 ? 'event' : 'events'} here{' '}
                      {past === 1 ? 'has' : 'have'} already happened.
                    </span>
                    <span className="hidden md:inline">{past} already happened</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      track('filter_past_toggled', { selecting: true, from: 'empty_day' })
                      onShowPast()
                    }}
                    className="rounded-md border border-hairline px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-paper md:px-1.5 md:py-0.5 md:text-[10px] dark:border-slate-800 dark:text-slate-300 dark:hover:bg-slate-800"
                  >
                    Show past events
                  </button>
                </div>
              ) : dayEvents.length === 0 ? (
                <div className="flex items-center justify-center py-2 md:h-full md:py-0">
                  {/* On DESKTOP an empty day is one cell of seven, so a dash reads
                      fine. On MOBILE the day IS the whole screen, and a single
                      faint dash reads as "this thing is broken" rather than
                      "nothing on today" - say it in words there. */}
                  <span className="text-[11px] text-slate-300 dark:text-slate-700 md:inline hidden">
                    {'—'}
                  </span>
                  <span className="px-3 py-6 text-center text-xs text-slate-400 md:hidden dark:text-slate-500">
                    Nothing listed for this day yet.
                    <br />
                    New events go up every Tuesday.
                  </span>
                </div>
              ) : (
                dayEvents.map((e, i) => (
                  <Fragment key={e.id}>
                    {/* The phone's Day view uses the AGENDA row (Andrew,
                        2026-09-08), not the grid card. A phone day list is
                        full-width, so it has the room the 3-day column never had:
                        a fixed time column the eye can run down, and the venue
                        under the title. The card layout only earns its shape in a
                        137px column. */}
                    {isMobile ? (
                      <AgendaRow
                        event={e}
                        onClick={() => onOpenEvent(e)}
                        views={popularOpens?.get(popularKey(e))}
                      />
                    ) : (
                      <EventCard event={e} onClick={() => onOpenEvent(e)} />
                    )}
                    {/* Signup drops in after the 10th event, or after the last one
                        on a thin day so it always appears exactly once. Asking for
                        an email only once someone has scrolled ten events deep -
                        i.e. once the calendar has proved useful - rather than
                        before they've seen anything. Mobile only; desktop keeps
                        the inline top-bar form. */}
                    {isMobile &&
                      subscribeSlot &&
                      i === Math.min(9, dayEvents.length - 1) && (
                        <div className="pt-1">{subscribeSlot}</div>
                      )}
                  </Fragment>
                ))
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// What sits beside the time. A price wins when we have one; otherwise cost_type
// carries it. Null means we genuinely don't know, and we then say NOTHING - a
// wrong "FREE" is far worse than a blank, because it sends someone out the door
// expecting not to pay. Prices are read from the weekly digest, which is the
// source of truth; the calendar's own note field is a lossy copy of it.
// Tones track COST_BAR, so the word beside the time is the same colour as the bar
// down the side of the card rather than a second, unrelated palette. Only these
// two cost types ever become text - a priced event shows its price in slate - so
// only these two have to hold AA at 10px.
function costLabel(event: EventRow): { text: string; tone: string } | null {
  if (event.cost_type === 'free')
    return { text: 'FREE', tone: 'text-free' }
  if (event.price_text)
    return { text: event.price_text, tone: 'text-slate-500 dark:text-slate-400' }
  if (event.cost_type === 'promo')
    return { text: 'DEAL', tone: 'text-promo' }
  return null
}

// The popup's version of the same answer. A card can afford ONE token beside the
// time, so costLabel() picks either the word or the price and a $75 class ended
// up opening a popup with no price anywhere on it. A row has space for both: the
// key's word (Free / Paid / Deal) against its own colour, plus the amount when
// we publish one.
//
// It says MORE than a card in one case on purpose - a ticketed event with no
// published price reads "Paid" here, where a card shows nothing and leans on the
// bar down its side. The popup has no bar, so without this the detail view knew
// less about cost than the card you tapped to reach it.
//
// A null cost_type still says nothing, for the reason it says nothing on a card:
// we don't know, and a guessed FREE sends someone out the door expecting not to
// pay. A price with no type shows the price alone.
// Tones follow the same rule as the key: ticketed is the lightest step of the
// ladder and never renders as text, so its word rides the row's own slate and
// only the dot carries the colour.
function costDetail(
  event: EventRow,
): { label: string | null; price: string | null; tone: string } | null {
  if (!event.cost_type && !event.price_text) return null
  const entry = KEY_ENTRIES.find((k) => k.type === event.cost_type)
  return {
    label: entry?.label ?? null,
    price: event.price_text ?? null,
    tone:
      event.cost_type === 'free'
        ? 'font-semibold text-free'
        : event.cost_type === 'promo'
          ? 'font-semibold text-promo'
          : '',
  }
}

// The left bar carries COST, not the host. The three colours are a LIGHTNESS
// LADDER (L* 22 / 41 / 60) defined once in index.css as --color-free /
// --color-ticketed / --color-promo. Read the note there before changing any of
// them: the spacing between them is the design, not the hues. They were a
// hue-only trio until 7/28, which Lisa reported as "not distinct" and which
// measured dE 4.5 between two of them under deuteranopia - i.e. the same colour.
const COST_BAR: Record<CostType, string> = {
  free: 'bg-free',
  ticketed: 'bg-ticketed',
  promo: 'bg-promo',
}
// null is "we don't know what it costs yet", and a wrong green is worse than no
// colour at all - it sends someone out the door expecting not to pay. Same reason
// costLabel() says nothing for a null: neutral, never guessed into a bucket.
// Warm now rather than slate: it was the last cool grey among the cost marks, and
// it has to stay obviously lighter than ticketed (L* 89 vs 60) so "we don't know"
// never reads as "you pay".
const UNKNOWN_BAR = 'bg-unknown dark:bg-slate-700'

// One dot color for every surface that marks an event: cost, not host.
function costDot(event: EventRow): string {
  return event.cost_type ? COST_BAR[event.cost_type] : UNKNOWN_BAR
}

function EventCard({ event, onClick }: { event: EventRow; onClick: () => void }) {
  const cost = costLabel(event)
  const locality = localityForVenue(event.venue ?? null)
  const town = locality === CITY ? null : locality
  const bar = costDot(event)
  // Read-only here - a tiny badge that shows only for a favorited event, never
  // its own click target. This card is a single button covering the whole
  // tile (opens the popup), and the grid cells are too small (137px, 11px
  // type) to carve out a second independent tap target without real clutter.
  // The toggle itself lives in the popup one tap away.
  const { isFavorited } = useFavorites()
  const favorited = isFavorited(event.id)
  // An <a> with a real href, not a <button>. The plain click still opens the
  // popup - preventDefault below - but the href is what a crawler follows, and
  // until this existed the ~1650 pre-rendered event and day pages were reachable
  // only from the sitemap. See lib/staticPaths for the whole story.
  return (
    <a
      href={eventPath(event)}
      onClick={(e) => {
        if (wantsNewContext(e)) return
        e.preventDefault()
        onClick()
      }}
      className="flex w-full items-stretch gap-1.5 rounded-md bg-white p-2 text-left text-slate-900 transition hover:bg-slate-50 md:p-1"
    >
      <span className={`w-1 shrink-0 rounded-full ${bar}`} />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium leading-snug md:line-clamp-2 md:text-[11px] md:leading-tight">
          {favorited && (
            <Star
              aria-hidden="true"
              className="mr-1 inline h-3 w-3 shrink-0 -translate-y-px fill-amber-400 text-amber-400 md:h-2.5 md:w-2.5"
            />
          )}
          {event.name}
        </span>
        <span className="mt-0.5 flex items-baseline gap-1.5 text-xs md:text-[10px]">
          <span className="min-w-0 shrink-0 truncate opacity-80">
            {fmtRange(event.start_time, event.end_time)}
          </span>
          {/* The town, right of the time, and ONLY when it is not Lynchburg
              (Andrew, 2026-09-08). This card is the 3-day column and the phone's
              day list, neither of which has room for the venue - so a Bedford or
              Amherst event looked local until you opened it. Lynchburg is the
              default and saying so on most of the rows would be noise. */}
          {town && (
            <span className="min-w-0 truncate text-slate-500 dark:text-slate-400">{town}</span>
          )}
          {cost && (
            <span className={`ml-auto shrink-0 font-semibold tabular-nums ${cost.tone}`}>
              {cost.text}
            </span>
          )}
        </span>
      </span>
    </a>
  )
}

// ---- Agenda view ----
//
// One column, every matching event from the anchor day forward, in time order,
// under a heading per day. The grid views answer "what's on Thursday"; this one
// answers "what's coming up", which is the question you have when you don't
// already know the day - and the only way to read a run of days on a phone
// without tapping through them one at a time.
//
// It's also the only view with room for the VENUE. A 3-day column is 137px wide
// and gives a title two clamped lines at 11px; a full-width row fits the title,
// the time and the place, which is most of what people open the popup for.

/**
 * The venue with the town removed, for the rows that already lead with it.
 *
 * Most venue strings carry their town - "Forest Library, Forest", "Devils
 * Backbone Basecamp and Meadows, Roseland" - so printing the town in front of
 * them said it twice in one row.
 *
 * Works on comma-separated SEGMENTS rather than a trailing suffix, because the
 * town is not always last: "National D-Day Memorial, 3 Overlord Circle, Bedford,
 * VA 24523" buries it before the state and zip. A bare state/zip segment goes
 * too - it is noise once the town is stated, and leaving it behind produced
 * "...3 Overlord Circle, VA 24523".
 *
 * Returns null when nothing is left, which is a venue that was only ever its own
 * town ("Bedford"); the caller then prints the town alone rather than twice.
 */
function venueWithoutTown(venue: string, town: string | null): string | null {
  const kept = venue
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean)
    // The town, but only when the row is already printing it in front.
    .filter((p) => !town || p.toLowerCase() !== town.toLowerCase())
    // State and zip go on EVERY row, town or not (Andrew, 2026-09-08). "VA
    // 24523" is not something a reader of a Lynchburg-area calendar needs, and
    // it was surviving on Lynchburg venues, which never get a town prefix and so
    // never went through the filter above.
    .filter((p) => !/^[A-Za-z]{2}(\s+\d{5}(-\d{4})?)?$/.test(p))
  return kept.length > 0 ? kept.join(', ') : null
}

function AgendaRow({
  event,
  onClick,
  views,
}: {
  event: EventRow
  onClick: () => void
  /** Distinct people, from the Popular filter (lib/popularity). Undefined outside
      Popular AND for any event Popular doesn't know about - either way,
      no badge. See CalendarViewProps.popularOpens in types.ts. */
  views?: number
}) {
  const cost = costLabel(event)
  const bar = costDot(event)
  const locality = localityForVenue(event.venue ?? null)
  const town = locality === CITY ? null : locality
  const venueText = event.venue
    ? venueWithoutTown(event.venue, town ? String(town) : null)
    : null
  // Same read-only badge as EventCard - see its comment for why the toggle
  // itself stays in the popup rather than a second tap target on this row.
  const { isFavorited } = useFavorites()
  const favorited = isFavorited(event.id)
  // Same reasoning as EventCard: a real href so the event's own page is
  // reachable, the plain click still opens the popup.
  return (
    <a
      href={eventPath(event)}
      onClick={(e) => {
        if (wantsNewContext(e)) return
        e.preventDefault()
        onClick()
      }}
      className="flex w-full items-start gap-2 rounded-md p-2 text-left text-slate-900 transition hover:bg-slate-50 dark:text-slate-100 dark:hover:bg-slate-800"
    >
      <span className={`h-8 w-1 shrink-0 rounded-full ${bar}`} />
      {/* Fixed-width and tabular, so the times line up down the page. That
          column IS the chronological read - without it the eye has to find the
          time inside each row, which is the thing a list is supposed to save.
          84px because the longest range the data produces is "10:30a–11:30a" at
          roughly 80px; a narrower column wrapped every half-hour range onto two
          lines and made the rows lurch. */}
      {/* The cost sits UNDER the time in this same column (Andrew, 2026-09-08),
          not out on the right edge. Both are short, both are tabular, and
          stacking them hands the whole rest of the row to the title and the
          venue - which were the two things being truncated. */}
      <span className="w-[5.25rem] shrink-0 pt-0.5 sm:w-24">
        <span className="block text-xs tabular-nums text-slate-500 dark:text-slate-400">
          {fmtRange(event.start_time, event.end_time)}
        </span>
        {cost && (
          <span className={`mt-0.5 block text-xs font-semibold tabular-nums ${cost.tone}`}>
            {cost.text}
          </span>
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium leading-snug">
          {favorited && (
            <Star
              aria-hidden="true"
              className="mr-1 inline h-3 w-3 shrink-0 -translate-y-px fill-amber-400 text-amber-400"
            />
          )}
          {event.name}
          {/* Only renders when the caller passed `views` - i.e. only while
              Popular is active and only for a name it actually has a count
              for. Quieter than the title (font-normal vs the title's medium,
              a step lighter on both palettes) but not faint: slate-600 on
              white measures 7.58:1, slate-400 on slate-900 measures 6.97:1,
              both well past the 4.5:1 AA floor for text this size
              (2026-09-11). */}
          {views !== undefined && (
            <span className="ml-1 font-normal text-slate-600 dark:text-slate-400">
              ({views} {views === 1 ? 'view' : 'views'})
            </span>
          )}
        </span>
        {(venueText || town) && (
          <span className="mt-0.5 block truncate text-xs text-slate-500 dark:text-slate-400">
            {/* The town leads, left of the venue (Andrew, 2026-09-08), and only
                when it is not Lynchburg. Where a thing is scopes whether the
                venue name is worth reading at all: "Bedford · Bower Center"
                tells a Lynchburg reader to skip it before they parse the name.
                The separator only prints when something follows it - a venue
                that was only its own town leaves the town standing alone. */}
            {town && (
              <span className="font-medium text-slate-600 dark:text-slate-300">
                {town}
                {venueText ? ' · ' : ''}
              </span>
            )}
            {venueText}
          </span>
        )}
      </span>
    </a>
  )
}

function AgendaList({
  anchorDate,
  events,
  hiddenPast,
  onShowPast,
  subscribeSlot,
  loading,
  onOpenEvent,
  popularOpens,
}: {
  anchorDate: Date
  /** Already sorted by start time - the grouping below relies on it. */
  events: EventRow[]
  hiddenPast: EventRow[]
  onShowPast: () => void
  subscribeSlot?: React.ReactNode
  loading?: boolean
  onOpenEvent: (e: EventRow) => void
  /** Only set while Popular is active - see CalendarViewProps.popularOpens. */
  popularOpens?: Map<string, number>
}) {
  const isMobile = useIsMobile()
  const fromMs = startOfDay(anchorDate).getTime()

  const groups = useMemo(() => {
    const m = new Map<number, { day: Date; events: EventRow[] }>()
    for (const e of events) {
      const start = new Date(e.start_time)
      const day = startOfDay(start)
      // Forward from the anchor. The arrows, Today and the date picker all move
      // that anchor, so "go back a week" still works here - it just moves where
      // the list BEGINS rather than sliding a fixed window.
      if (day.getTime() < fromMs) continue
      let g = m.get(day.getTime())
      if (!g) {
        g = { day, events: [] }
        m.set(day.getTime(), g)
      }
      g.events.push(e)
    }
    return [...m.values()].sort((a, b) => a.day.getTime() - b.day.getTime())
  }, [events, fromMs])

  const total = groups.reduce((n, g) => n + g.events.length, 0)
  // Same rule as the day list: ask for an email once the calendar has proved
  // useful, not before. Counted across the whole list rather than per day.
  const subscribeAfter = Math.min(9, total - 1)
  let seen = -1

  return (
    <div className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-hairline bg-white md:h-full dark:border-slate-800 dark:bg-slate-900">
      <div className="min-h-0 flex-1 md:overflow-y-auto">
        {loading && total === 0 ? (
          /* Same reasoning as the grid: never paint an empty state over a fetch
             that hasn't come back. See WeekGrid. */
          <div aria-hidden="true" className="space-y-1 p-2">
            {Array.from({ length: 10 }, (_, i) => (
              <div
                key={i}
                className="h-12 animate-pulse rounded-md bg-slate-100 dark:bg-slate-800"
                style={{ opacity: 1 - i * 0.07 }}
              />
            ))}
          </div>
        ) : total === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 px-4 py-10 text-center">
            {hiddenPast.length > 0 ? (
              <>
                <span className="text-sm text-slate-400 dark:text-slate-500">
                  Nothing coming up from this date. {hiddenPast.length}{' '}
                  {hiddenPast.length === 1 ? 'event has' : 'events have'} already happened.
                </span>
                <button
                  type="button"
                  onClick={() => {
                    track('filter_past_toggled', { selecting: true, from: 'agenda_empty' })
                    onShowPast()
                  }}
                  className="rounded-md border border-hairline px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-paper dark:border-slate-800 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  Show past events
                </button>
              </>
            ) : (
              <span className="text-sm text-slate-400 dark:text-slate-500">
                Nothing listed from this date yet.
                <br />
                New events go up every Tuesday.
              </span>
            )}
          </div>
        ) : (
          groups.map(({ day, events: dayEvents }) => {
            const today = isToday(day)
            return (
              <section key={day.toISOString()}>
                {/* Sticky only from md, where this container is the scrollport.
                    On a phone the PAGE scrolls and the calendar header is already
                    sticky at z-20, so a second sticky bar here would stack under
                    it and cover the first row of each day. */}
                <h2 className="z-10 flex items-center gap-2 border-b border-hairline bg-paper px-3 py-1.5 md:sticky md:top-0 dark:border-slate-800 dark:bg-slate-950">
                  <span
                    className={`inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-xs font-semibold ${
                      today ? 'bg-brick-600 text-white' : 'text-slate-700 dark:text-slate-200'
                    }`}
                  >
                    {format(day, 'd')}
                  </span>
                  <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                    {format(day, 'EEEE, MMMM d')}
                  </span>
                  <span className="ml-auto text-xs text-slate-600 dark:text-slate-500">
                    {dayEvents.length}
                  </span>
                </h2>
                <div className="space-y-0.5 p-1">
                  {dayEvents.map((e) => {
                    seen += 1
                    return (
                      <Fragment key={e.id}>
                        <AgendaRow
                          event={e}
                          onClick={() => onOpenEvent(e)}
                          views={popularOpens?.get(popularKey(e))}
                        />
                        {isMobile && subscribeSlot && seen === subscribeAfter && (
                          <div className="p-1">{subscribeSlot}</div>
                        )}
                      </Fragment>
                    )
                  })}
                </div>
              </section>
            )
          })
        )}
      </div>
    </div>
  )
}

// ---- Month view ----

interface MonthGridProps extends GridProps {
  onOpenDay: (day: Date, events: EventRow[]) => void
}

function MonthGrid({
  anchorDate,
  eventsForDay,
  onOpenEvent,
  onOpenDay,
}: MonthGridProps) {
  const { start: gridStart, end: gridEnd } = monthGridRange(anchorDate)
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd })
  const rows = days.length / 7

  return (
    <div className="flex h-[60vh] min-h-0 flex-col overflow-hidden rounded-lg border border-hairline bg-white md:h-full dark:border-slate-800 dark:bg-slate-900">
      <div className="grid shrink-0 grid-cols-7 border-b border-hairline dark:border-slate-800">
        {WEEKDAY_LABELS.map((d) => (
          <div
            key={d}
            className="px-1 py-1 text-center text-[10px] font-medium uppercase tracking-wide text-slate-600 dark:text-slate-500"
          >
            {d}
          </div>
        ))}
      </div>
      <div
        className="grid min-h-0 flex-1 grid-cols-7"
        style={{ gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))` }}
      >
        {days.map((day) => {
          const dayEvents = eventsForDay(day)
          const inMonth = isSameMonth(day, anchorDate)
          const today = isToday(day)
          const visible = dayEvents.slice(0, MONTH_MAX_CHIPS)
          const extra = dayEvents.length - visible.length
          return (
            <div
              key={day.toISOString()}
              className={`flex min-h-0 flex-col overflow-hidden border-b border-r border-hairline p-1 dark:border-slate-800 ${
                inMonth
                  ? isWeekend(day)
                    ? 'bg-brick-50 dark:bg-brick-900/20'
                    : ''
                  : 'bg-paper dark:bg-slate-900/40'
              }`}
            >
              <div className="mb-0.5 flex shrink-0 justify-center md:justify-end">
                <span
                  className={`inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[11px] font-semibold ${
                    today
                      ? 'bg-brick-600 text-white'
                      : inMonth
                      ? 'text-slate-700 dark:text-slate-200'
                      : 'text-slate-400 dark:text-slate-600'
                  }`}
                >
                  {format(day, 'd')}
                </span>
              </div>

              {/* Below md a cell is too narrow for titles, so it shows dots and
                  opens the day's list on tap. */}
              {dayEvents.length > 0 && (
                <button
                  type="button"
                  onClick={() => onOpenDay(day, dayEvents)}
                  aria-label={`${dayEvents.length} event${
                    dayEvents.length === 1 ? '' : 's'
                  } on ${format(day, 'MMMM d')}`}
                  className="flex min-h-0 flex-1 flex-wrap content-start items-center justify-center gap-0.5 rounded p-0.5 transition-colors hover:bg-slate-100 md:hidden dark:hover:bg-slate-800"
                >
                  {dayEvents.slice(0, 4).map((e) => (
                    <span
                      key={e.id}
                      className={`h-1.5 w-1.5 rounded-full ${costDot(e)}`}
                    />
                  ))}
                  {dayEvents.length > 4 && (
                    <span className="text-[9px] font-medium leading-none text-slate-400 dark:text-slate-500">
                      +{dayEvents.length - 4}
                    </span>
                  )}
                </button>
              )}

              <div className="hidden min-h-0 flex-1 space-y-0.5 overflow-hidden md:block">
                {visible.map((e) => {
                  return (
                    <button
                      key={e.id}
                      type="button"
                      onClick={() => onOpenEvent(e)}
                      className="flex w-full items-center gap-1 rounded px-1 py-0.5 text-left hover:bg-slate-100 dark:hover:bg-slate-800"
                    >
                      <span
                        className={`h-1.5 w-1.5 shrink-0 rounded-full ${costDot(e)}`}
                      />
                      <span className="truncate text-[10px] text-slate-600 dark:text-slate-300">
                        <span className="font-medium text-slate-500 dark:text-slate-400">
                          {fmtTime(e.start_time)}
                        </span>{' '}
                        {e.name}
                      </span>
                    </button>
                  )
                })}
                {/* No count here on purpose (Andrew, 2026-09-11). A busy day read
                    "+86 more", which is noise rather than information: the number
                    is an artifact of how many chips happen to fit, not something a
                    reader can act on. The BUTTON stays, because on desktop it is
                    the only way into the day's full list, and dropping it would
                    strand every event past the third. */}
                {extra > 0 && (
                  <button
                    type="button"
                    onClick={() => onOpenDay(day, dayEvents)}
                    aria-label={`See all ${dayEvents.length} events on ${format(day, 'MMMM d')}`}
                    className="w-full rounded px-1 py-0.5 text-left text-[10px] font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                  >
                    See all
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ---- Modal (event detail + day list, shared shell) ----

function Modal({
  state,
  onClose,
  onOpenEvent,
}: {
  state: NonNullable<ModalState>
  onClose: () => void
  onOpenEvent: (e: EventRow) => void
}) {
  // Favouriting lives up here beside the close button (Andrew, 2026-09-08),
  // rather than down in the add-to-calendar row where it used to sit. It is a
  // property OF the event, not one more thing to do with it, and the top-right
  // corner is where that reads.
  const { isFavorited, toggleFavorite } = useFavorites()
  const favorited = state.kind === 'event' && isFavorited(state.event.id)

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 md:p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative z-10 max-h-[85vh] w-full max-w-md overflow-y-auto rounded-lg border border-slate-200 bg-white p-4 shadow-xl dark:border-slate-800 dark:bg-slate-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute right-1 top-1 flex items-center gap-0.5 md:right-2 md:top-2">
          {state.kind === 'event' && (
            <button
              type="button"
              aria-pressed={favorited}
              title={favorited ? 'Remove from Favorited Events' : 'Add to Favorited Events'}
              onClick={() => toggleFavorite(state.event.id, state.event.name)}
              className="inline-flex min-h-10 min-w-10 items-center justify-center rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 md:min-h-0 md:min-w-0 dark:hover:bg-slate-800 dark:hover:text-slate-200"
            >
              <Star
                aria-hidden="true"
                className={`h-4 w-4 ${favorited ? 'fill-amber-400 text-amber-400' : ''}`}
              />
            </button>
          )}
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="inline-flex min-h-10 min-w-10 items-center justify-center rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 md:min-h-0 md:min-w-0 dark:hover:bg-slate-800 dark:hover:text-slate-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {state.kind === 'event' ? (
          <EventDetail event={state.event} />
        ) : (
          <DayDetail
            day={state.day}
            events={state.events}
            onOpenEvent={onOpenEvent}
          />
        )}

        {/* Desktop only. A phone has no Esc key, so on mobile this would be a
            line of chrome describing something the reader cannot do. */}
        <p className="mt-3 hidden border-t border-slate-200 pt-2 text-center text-xs text-slate-400 md:block dark:border-slate-800 dark:text-slate-500">
          Clicking{' '}
          <kbd className="rounded border border-slate-300 bg-slate-100 px-1.5 py-0.5 font-sans text-[11px] font-medium text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
            Esc
          </kbd>{' '}
          closes modal
        </p>
      </div>
    </div>
  )
}

// Shared by the two add-to-calendar controls, one of which is an anchor and the
// other a button. min-h-9 only below md: 80% of the traffic here is a phone, and
// this is the action someone opens the popup to take, so it gets a real tap
// target. Desktop drops back to the copy row's height so the footer stays level.
const ADD_BTN =
  'inline-flex min-h-9 items-center gap-1.5 rounded-md border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-100 md:min-h-0 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800'

function EventDetail({ event }: { event: EventRow }) {
  const palette = hostColor(event.host)
  const start = new Date(event.start_time)
  const detail = costDetail(event)
  const [copied, setCopied] = useState<'details' | 'url' | null>(null)
  // Favouriting moved to the modal header on 2026-09-08, so this component no
  // longer reads or writes it.

  const copy = async (kind: 'details' | 'url') => {
    const cost = costLabel(event)
    const text =
      kind === 'url'
        ? (event.link ?? '')
        : [
            event.name,
            `${format(start, 'EEEE, MMMM d, yyyy')} · ${fmtRange(event.start_time, event.end_time)}`,
            // Right under the date here too. Pasting a course into a text
            // message without its run is how someone books one Wednesday.
            event.session_dates,
            event.venue,
            cost?.text,
            event.link,
            event.note,
          ]
            .filter(Boolean)
            .join('\n')
    try {
      await navigator.clipboard.writeText(text)
      setCopied(kind)
      track('event_copied', { kind, event_name: event.name })
      setTimeout(() => setCopied(null), 1600)
    } catch {
      // Clipboard can be denied (permissions policy); nothing useful to do.
    }
  }

  return (
    <div>
      <h2 className="pr-10 md:pr-6 text-base font-semibold text-slate-900 dark:text-slate-100">
        {event.name}
      </h2>
      {event.host && (
        <span
          className={`mt-1.5 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${palette.chip}`}
        >
          {hostLabel(event.host)}
        </span>
      )}
      {/* Additional kinds (lib/kinds): an open mic that is also Comedy shows both
          chips, each in its own kind's colour, so the reader who found it under
          Comedy sees why it is here. The host chip stays first - it is the primary. */}
      {extraKinds(event).map((kind) => (
        <span
          key={kind}
          className={`ml-1 mt-1.5 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${hostColor(kind).chip}`}
        >
          {hostLabel(kind)}
        </span>
      ))}
      <div className="mt-3 space-y-2 text-sm text-slate-600 dark:text-slate-300">
        {/* A registered course keeps ONE row, at its first session, so this date
            is the start of a run and not the whole of it. session_dates is the
            rest, and it sits directly under the date because that is the line it
            changes the meaning of - a reader who takes "Wednesday, November 4"
            at face value has missed that they are committing to four Wednesdays.
            Smaller and muted so the date still reads first. Null on every
            one-off and every drop-in, which is nearly every row. */}
        <div className={`flex gap-2 ${event.session_dates ? 'items-start' : 'items-center'}`}>
          <CalendarDays
            className={`h-4 w-4 shrink-0 text-slate-400 ${event.session_dates ? 'mt-0.5' : ''}`}
          />
          <span className="min-w-0">
            <span className="block">
              {format(start, 'EEEE, MMMM d, yyyy')} ·{' '}
              {fmtRange(event.start_time, event.end_time)}
            </span>
            {event.session_dates && (
              <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">
                {event.session_dates}
              </span>
            )}
          </span>
        </div>
        {/* Straight under the date, because when and what it costs are the two
            things someone decides on. The dot is the same mark the cards and the
            header key use, so the colour means here what it means everywhere. */}
        {detail && (
          <div className="flex items-center gap-2" aria-label="What it costs">
            <Ticket className="h-4 w-4 shrink-0 text-slate-400" />
            <span className="flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className={`h-2 w-2 shrink-0 rounded-full ${costDot(event)}`}
              />
              {detail.label && <span className={detail.tone}>{detail.label}</span>}
              {detail.label && detail.price && (
                <span aria-hidden="true" className="text-slate-300 dark:text-slate-600">
                  ·
                </span>
              )}
              {detail.price && (
                <span className="font-semibold tabular-nums text-slate-700 dark:text-slate-200">
                  {detail.price}
                </span>
              )}
            </span>
          </div>
        )}
        {event.venue && (
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 shrink-0 text-slate-400" />
            {/* Linked only when the venue actually HAS a page (3+ upcoming
                events, same rule the generator writes them by). Linking the rest
                would land on the SPA catch-all, which answers 200 with the app
                and reads as a dead end. */}
            {venuePathFor(event.venue) ? (
              <a
                href={venuePathFor(event.venue) as string}
                className="text-brick-600 underline underline-offset-2 hover:text-brick-700 dark:text-brick-300"
              >
                {event.venue}
              </a>
            ) : (
              <span>{event.venue}</span>
            )}
          </div>
        )}
        {event.link && (
          <div className="flex items-center gap-2">
            <LinkIcon className="h-4 w-4 shrink-0 text-slate-400" />
            <a
              href={event.link}
              target="_blank"
              rel="noreferrer"
              onClick={() =>
                track('event_link_clicked', {
                  event_name: event.name,
                  host: event.host,
                  // venue rides along so opens and click-throughs can be read
                  // against the same breakdown. event_opened already carries
                  // it; without it here the funnel loses its venue dimension.
                  venue: event.venue,
                })
              }
              className="truncate text-brick-600 hover:underline dark:text-brick-300"
            >
              {linkLabel(event.link)}
            </a>
          </div>
        )}
        {event.note && (
          <div className="flex items-start gap-2">
            <StickyNote className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
            <span className="whitespace-pre-wrap">{event.note}</span>
          </div>
        )}
      </div>

      {/* One bordered footer holding both rows, so the modal ends with a single
          hairline rather than one per row. Add to calendar sits ABOVE copying:
          someone who has decided to go wants it on their own calendar, and
          copying the details is the fallback for everywhere else. */}
      <div className="mt-4 space-y-2 border-t border-slate-200 pt-3 dark:border-slate-800">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-slate-500 dark:text-slate-400">Add to calendar:</span>
          {/* Google opens a prefilled template in a new tab; Apple hands over an
              .ics file, which is what Outlook opens too. Both carry an icon,
              which is the whole visual difference from the copy row below - the
              same neutral chrome, so neither provider looks like the endorsed one. */}
          <a
            href={googleCalendarUrl(event)}
            target="_blank"
            rel="noreferrer"
            onClick={() =>
              track('event_add_to_calendar', {
                provider: 'google',
                event_name: event.name,
              })
            }
            className={ADD_BTN}
          >
            <CalendarPlus className="h-4 w-4 text-slate-400" />
            Google
          </a>
          <button
            type="button"
            title="Downloads an .ics file, which Outlook opens too"
            onClick={() => {
              track('event_add_to_calendar', {
                provider: 'apple',
                event_name: event.name,
              })
              downloadIcs(event)
            }}
            className={ADD_BTN}
          >
            <Download className="h-4 w-4 text-slate-400" />
            Apple
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-slate-500 dark:text-slate-400">Copy to Clipboard:</span>
          <button
            type="button"
            onClick={() => copy('details')}
            className="rounded-md border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            {copied === 'details' ? 'Copied' : 'All Details'}
          </button>
          <button
            type="button"
            onClick={() => copy('url')}
            disabled={!event.link}
            className="rounded-md border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-100 disabled:opacity-40 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            {copied === 'url' ? 'Copied' : 'Share URL'}
          </button>
        </div>
      </div>
    </div>
  )
}

function DayDetail({
  day,
  events,
  onOpenEvent,
}: {
  day: Date
  events: EventRow[]
  onOpenEvent: (e: EventRow) => void
}) {
  // Same read-only badge as EventCard/AgendaRow - the toggle stays in the
  // popup this row opens.
  const { isFavorited } = useFavorites()
  return (
    <div>
      <h2 className="pr-10 md:pr-6 text-base font-semibold text-slate-900 dark:text-slate-100">
        {format(day, 'EEEE, MMMM d')}
      </h2>
      <div className="mt-3 max-h-[60vh] space-y-1.5 overflow-y-auto">
        {events.map((e) => {
          // Same token the cards carry, so a day list read inside the popup is
          // not the one surface that drops the price.
          const cost = costLabel(e)
          return (
            <button
              key={e.id}
              type="button"
              onClick={() => onOpenEvent(e)}
              className="flex w-full items-start gap-2 rounded-md p-2 text-left ring-1 ring-inset ring-slate-200 transition hover:bg-slate-50 dark:ring-slate-700 dark:hover:bg-slate-800"
            >
              <span
                className={`mt-1 h-2 w-2 shrink-0 rounded-full ${costDot(e)}`}
              />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium leading-tight">
                  {isFavorited(e.id) && (
                    <Star
                      aria-hidden="true"
                      className="mr-1 inline h-3 w-3 shrink-0 -translate-y-px fill-amber-400 text-amber-400"
                    />
                  )}
                  {e.name}
                </span>
                <span className="mt-0.5 block text-xs opacity-80">
                  {fmtRange(e.start_time, e.end_time)}
                  {e.venue ? ` · ${e.venue}` : ''}
                </span>
              </span>
              {cost && (
                <span
                  className={`shrink-0 text-xs font-semibold tabular-nums ${cost.tone}`}
                >
                  {cost.text}
                </span>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
